import React, { useState } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import { Link, useLocation, Navigate } from 'react-router-dom';
import { RoleIndicator } from './PermissionGuard';

function AdminLayout({ children }) {
  const { user, logout, isAuthenticated, loading, canManageUsers, canManageContent } = useAdmin();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Redirect to login if not authenticated
  if (!loading && !isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="admin-loading d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-3">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  const allMenuItems = [
    {
      path: '/admin/dashboard',
      icon: 'fas fa-tachometer-alt',
      label: 'Dashboard',
      description: 'Overview & Statistics',
      requiredRole: 'viewer'
    },
    {
      path: '/admin/categories',
      icon: 'fas fa-list',
      label: 'Categories',
      description: 'Menu Categories',
      requiredRole: 'editor'
    },
    {
      path: '/admin/menu-items',
      icon: 'fas fa-utensils',
      label: 'Menu Items',
      description: 'Food & Beverages',
      requiredRole: 'editor'
    },
    {
      path: '/admin/restaurant-info',
      icon: 'fas fa-info-circle',
      label: 'Restaurant Info',
      description: 'Contact & Details',
      requiredRole: 'editor'
    },
    {
      path: '/admin/page-photos',
      icon: 'fas fa-images',
      label: 'Page Photos',
      description: 'Website Images',
      requiredRole: 'editor'
    },
    {
      path: '/admin/users',
      icon: 'fas fa-users',
      label: 'Users',
      description: 'Admin Accounts',
      requiredRole: 'admin'
    }
  ];

  // Filter menu items based on user permissions
  const menuItems = allMenuItems.filter(item => {
    if (!item.requiredRole) return true;
    
    const roleHierarchy = {
      'viewer': 1,
      'editor': 2,
      'admin': 3
    };
    
    const userLevel = roleHierarchy[user?.role] || 0;
    const requiredLevel = roleHierarchy[item.requiredRole] || 0;
    
    return userLevel >= requiredLevel;
  });

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
    }
  };

  return (
    <div className="admin-layout">
      {/* Sidebar */}
      <div className={`admin-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <div className="brand">
            <img src="/logo192.png" alt="Cioconat" className="brand-logo" />
            {!sidebarCollapsed && (
              <div className="brand-text">
                <h5 className="mb-0">Cioconat</h5>
                <small>CMS Admin</small>
              </div>
            )}
          </div>
          <button 
            className="sidebar-toggle"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            <i className={`fas ${sidebarCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'}`}></i>
          </button>
        </div>

        <nav className="sidebar-nav">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
              title={sidebarCollapsed ? item.label : ''}
            >
              <i className={item.icon}></i>
              {!sidebarCollapsed && (
                <div className="nav-text">
                  <span className="nav-label">{item.label}</span>
                  <small className="nav-description">{item.description}</small>
                </div>
              )}
            </Link>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className={`user-info ${sidebarCollapsed ? 'collapsed' : ''}`}>
            <div className="user-avatar">
              <i className="fas fa-user-circle"></i>
            </div>
            {!sidebarCollapsed && (
              <div className="user-details">
                <div className="user-name">{user?.username}</div>
                <div className="user-role">
                  <RoleIndicator showIcon={false} />
                </div>
              </div>
            )}
          </div>
          <button 
            className="logout-btn"
            onClick={handleLogout}
            title="Logout"
          >
            <i className="fas fa-sign-out-alt"></i>
            {!sidebarCollapsed && <span>Logout</span>}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className={`admin-main ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        {/* Top Bar */}
        <div className="admin-topbar">
          <div className="topbar-left">
            <h4 className="page-title mb-0">
              {menuItems.find(item => isActive(item.path))?.label || 'Admin Panel'}
            </h4>
          </div>
          <div className="topbar-right">
            <div className="topbar-item">
              <i className="fas fa-clock me-2"></i>
              {new Date().toLocaleString()}
            </div>
            <div className="topbar-item">
              <Link to="/" className="btn btn-outline-primary btn-sm">
                <i className="fas fa-external-link-alt me-2"></i>
                View Website
              </Link>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="admin-content">
          {children}
        </div>
      </div>

      <style jsx>{`
        .admin-layout {
          display: flex;
          min-height: 100vh;
          background-color: #f8f9fa;
        }

        .admin-sidebar {
          width: 280px;
          background: linear-gradient(180deg, #2c3e50 0%, #34495e 100%);
          color: white;
          transition: all 0.3s ease;
          position: fixed;
          height: 100vh;
          overflow-y: auto;
          z-index: 1000;
        }

        .admin-sidebar.collapsed {
          width: 80px;
        }

        .sidebar-header {
          padding: 1.5rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .brand {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .brand-logo {
          width: 40px;
          height: 40px;
          border-radius: 50%;
        }

        .brand-text h5 {
          color: white;
          font-weight: 600;
        }

        .brand-text small {
          color: rgba(255, 255, 255, 0.7);
        }

        .sidebar-toggle {
          background: none;
          border: none;
          color: white;
          font-size: 0.9rem;
          cursor: pointer;
          opacity: 0.7;
          transition: opacity 0.3s ease;
        }

        .sidebar-toggle:hover {
          opacity: 1;
        }

        .sidebar-nav {
          padding: 1rem 0;
        }

        .nav-item {
          display: flex;
          align-items: center;
          padding: 1rem 1.5rem;
          color: rgba(255, 255, 255, 0.8);
          text-decoration: none;
          transition: all 0.3s ease;
          gap: 1rem;
        }

        .nav-item:hover {
          background-color: rgba(255, 255, 255, 0.1);
          color: white;
          text-decoration: none;
        }

        .nav-item.active {
          background-color: #c9302c;
          color: white;
          border-right: 3px solid #fff;
        }

        .nav-item i {
          width: 20px;
          text-align: center;
          font-size: 1.1rem;
        }

        .nav-text {
          flex: 1;
        }

        .nav-label {
          display: block;
          font-weight: 500;
        }

        .nav-description {
          display: block;
          opacity: 0.7;
          font-size: 0.8rem;
        }

        .sidebar-footer {
          margin-top: auto;
          padding: 1rem;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .user-info {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1rem;
          padding: 0.5rem;
          background-color: rgba(255, 255, 255, 0.05);
          border-radius: 8px;
        }

        .user-info.collapsed {
          justify-content: center;
        }

        .user-avatar i {
          font-size: 2rem;
          color: rgba(255, 255, 255, 0.8);
        }

        .user-name {
          font-weight: 600;
          font-size: 0.9rem;
        }

        .user-role {
          font-size: 0.8rem;
          opacity: 0.7;
          text-transform: capitalize;
        }

        .logout-btn {
          width: 100%;
          background: none;
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: rgba(255, 255, 255, 0.8);
          padding: 0.75rem;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .logout-btn:hover {
          background-color: rgba(231, 76, 60, 0.2);
          border-color: #e74c3c;
          color: white;
        }

        .admin-main {
          flex: 1;
          margin-left: 280px;
          transition: all 0.3s ease;
        }

        .admin-main.sidebar-collapsed {
          margin-left: 80px;
        }

        .admin-topbar {
          background: white;
          padding: 1rem 2rem;
          border-bottom: 1px solid #e9ecef;
          display: flex;
          align-items: center;
          justify-content: space-between;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .page-title {
          color: #2c3e50;
          font-weight: 600;
        }

        .topbar-right {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .topbar-item {
          color: #6c757d;
          font-size: 0.9rem;
          display: flex;
          align-items: center;
        }

        .admin-content {
          padding: 2rem;
          min-height: calc(100vh - 80px);
        }

        @media (max-width: 768px) {
          .admin-sidebar {
            transform: translateX(-100%);
          }

          .admin-sidebar.show {
            transform: translateX(0);
          }

          .admin-main {
            margin-left: 0;
          }

          .admin-topbar {
            padding: 1rem;
          }

          .admin-content {
            padding: 1rem;
          }
        }
      `}</style>
    </div>
  );
}

export default AdminLayout;